import {
    Component,
    forceUpdate,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
    State,
} from "@stencil/core";
import { getCountryName, i18nProvider } from "@trumpf/ux.i18n";
import { fetchData } from "./api";
import { extractAnchorTags } from "./utils";

/**
 * @component ux-iws-sf-page-header
 * @description
 *   Component for the page header with navigation, logo, profile, and country selection.
 *   Supports internationalization and dynamic loading of navigation data.
 *
 * @example
 * <ux-iws-sf-page-header
 *   endpoint="/api/navigation"
 *   locale="de_DE"
 *   logo-link-url="/"
 *   logo-link-text="Trumpf"
 *   home-link-url="/home"
 *   home-link-text="Home"
 *   profile-link-text="Profile"
 * ></ux-iws-sf-page-header>
 *
 * @slot profile-links - Slot for additional profile links in the profile menu (`<a class="ux-tooltip__link-item">`)
 */
@Component({
    tag: "ux-iws-sf-page-header",
    styleUrl: "sf-page-header.scss",
    shadow: false,
})
export class SfPageHeader {
    /** Static cache for loaded navigation HTML (shared across all instances) */
    private static cachedNavigationHtml: string = "";
    /** ID for the i18n provider subscription */
    private i18nSubscriberId: string = "";
    /** Counter for navigation updates (used to refresh the key) */
    private navigationDataIncrement = 0;

    /** Reference to the host element */
    @Element() host: HTMLUxIwsSfPageHeaderElement;

    /** API endpoint used to fetch the navigation */
    @Prop() endpoint: string = "";

    /** Locale used to fetch data and translate texts */
    @Prop() locale: string = "de_DE";

    /** Link url for the TRUMPF logo */
    @Prop() logoLinkUrl: string = "/";

    /** Link text for the TRUMPF logo */
    @Prop() logoLinkText: string = "Trumpf";

    /** Link url for the site menu item */
    @Prop() homeLinkUrl: string = "";

    /** Link text for the site menu item */
    @Prop() homeLinkText: string = "";

    /** Text for the profile menu item */
    @Prop() profileLinkText: string = "";

    /** Storage for the fetched navigation HTML */
    @State() navigationHtml: string = "";

    /**
     * Custom event that is emitted when a meta nav item is clicked.
     * The `event.detail` provides additional info about the clicked nav item
     * (e.g. "locale-selection", "profile")
     */
    @Event({
        eventName: "uxMetaNavClick",
        composed: true,
        cancelable: false,
        bubbles: true,
    })
    metaNavClickEvent: EventEmitter<string>;

    componentWillLoad() {
        if (this.locale) {
            i18nProvider.changeLanguage(this.locale.replace("_", "-"));
        }
        if (SfPageHeader.cachedNavigationHtml) {
            this.navigationDataIncrement++;
            this.navigationHtml = SfPageHeader.cachedNavigationHtml;
        }
        if (this.endpoint) {
            fetchData(this.endpoint, this.locale).then((html) => {
                this.navigationDataIncrement++;
                this.navigationHtml = html;
                SfPageHeader.cachedNavigationHtml = html;
            });
        }
    }

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    /**
     * Returns the country code and name for the current locale.
     * @returns {string} Country name and code (e.g. "Germany | DE")
     * @private
     */
    private getCountryLabel = (): string => {
        const countryCode = this.locale
            .replace("-", "_")
            .split("_")[1]
            .toUpperCase();
        const countryName = getCountryName(countryCode);
        return countryName ? `${countryName} | ${countryCode}` : countryCode;
    };

    /**
     * Handler for clicks on meta navigation items.
     * Emits the uxMetaNavClick event.
     * @param {Event} event
     * @private
     */
    private handleClickMetaNavLink = (event: Event) => {
        event.preventDefault();
        const target: HTMLElement =
            (event.target as HTMLElement).closest(`[data-meta-navigation]`) ||
            (event.target as HTMLElement);
        this.metaNavClickEvent.emit(
            target?.getAttribute("data-meta-navigation") || ""
        );
    };

    /**
     * Renders the country selection link.
     * @returns {JSX.Element}
     * @private
     */
    private renderCountryLink = () => {
        const countryLabel = this.getCountryLabel();
        return (
            <div class="ux-iws-navigation-meta__item ux-iws-navigation-meta__item">
                <a
                    href="#"
                    onClick={this.handleClickMetaNavLink}
                    role="button"
                    data-meta-navigation="locale-selection"
                >
                    <span class="ux-iws-navigation-meta__icon-box">
                        <ux-icon name="nav-globe"></ux-icon>
                    </span>
                    <span class="ux-iws-navigation-meta__link-text">
                        {countryLabel}
                    </span>
                </a>
            </div>
        );
    };

    /**
     * Extracts the HTML content from the profile links slot.
     * @returns {string} HTML string of profile links
     * @private
     */
    private getProfileLinksHtml = (): string => {
        const html = Array.from(
            this.host.querySelectorAll<HTMLElement>("[slot='profile-links']")
        )
            .map((el) => el.outerHTML)
            .join("");
        return extractAnchorTags(html)
            .join("")
            .replace(/ slot="[^"]+"/gi, "");
    };

    /**
     * Renders the profile link with tooltip.
     * @returns {JSX.Element | null}
     * @private
     */
    private renderProfileLink = () => {
        if (!this.profileLinkText) {
            return null;
        }
        return (
            <div class="ux-iws-navigation-meta__item ux-iws-navigation-meta__item">
                <a
                    href="#"
                    onClick={this.handleClickMetaNavLink}
                    data-meta-navigation="profile"
                >
                    <span class="ux-iws-navigation-meta__icon-box">
                        <ux-icon name="avatar-circle"></ux-icon>
                    </span>
                    <span class="ux-iws-navigation-meta__link-text">
                        {this.profileLinkText}
                    </span>
                </a>
                <ux-tooltip
                    tooltip-target="a[data-meta-navigation='profile']"
                    data-id="tooltip-navi-profile"
                    align="top"
                >
                    <button class="button-close" data-ux-tooltip-close="">
                        <ux-icon name="close" class="ux-icon"></ux-icon>
                    </button>
                    <div innerHTML={this.getProfileLinksHtml()} />
                </ux-tooltip>
            </div>
        );
    };

    /**
     * Renders the home link, optionally as a dropdown variant.
     * @param {boolean} [dropdown=false] - Whether to render the dropdown variant.
     * @returns {JSX.Element | null}
     * @private
     */
    private renderHomeLink = (dropdown: boolean = false) => {
        if (!(this.homeLinkUrl && this.homeLinkText)) {
            return null;
        }
        return dropdown ? (
            <div class="ux-iws-navigation-meta__item">
                <a class="ux-button ux-button__trumpf" href={this.homeLinkUrl}>
                    {this.homeLinkText}
                    <ux-icon name="chevron-right"></ux-icon>
                </a>
            </div>
        ) : (
            <div class="ux-iws-navigation-meta__item ux-iws-navigation-meta__item--top-border">
                <a href={this.homeLinkUrl}>
                    <span class="ux-iws-navigation-meta__icon-box">
                        <ux-icon name="nav-home"></ux-icon>
                    </span>
                    <span class="ux-iws-navigation-meta__link-text">
                        {this.homeLinkText}
                    </span>
                </a>
            </div>
        );
    };

    render() {
        const { t } = i18nProvider;
        const menuLabel = t("app.header.menuLabel", "Menu");
        const closeLabel = t("app.header.closeButton", "Close");
        const backLabel = t("general.prevButton", "Back");
        return (
            <Host>
                <ux-iws-header type="mytrumpf">
                    <div slot="header" class="ux-iws-header__main-wrapper">
                        <a
                            class="ux-iws-header__logo"
                            href={this.logoLinkUrl}
                            aria-label={this.logoLinkText}
                        >
                            <img
                                src="https://www.trumpf.com/typo3conf/ext/trumpf_package/Resources/Public/Images/nav/logo.png"
                                alt="Trumpf logo"
                            />
                        </a>
                        <div class="ux-iws-header__navigation-wrapper">
                            <ux-iws-nav
                                back-button-text={backLabel}
                                key={
                                    this.navigationHtml
                                        ? `nav-${this.navigationDataIncrement}`
                                        : "loading"
                                }
                            >
                                <nav
                                    slot="navigation"
                                    innerHTML={this.navigationHtml}
                                />
                                <div
                                    slot="navigation-meta"
                                    class="ux-iws-nav__meta"
                                >
                                    <div class="ux-iws-nav__meta-container-header">
                                        <ux-iws-navigation-meta>
                                            {this.renderProfileLink()}
                                            {this.renderCountryLink()}
                                            {this.renderHomeLink()}
                                        </ux-iws-navigation-meta>
                                    </div>
                                    <div class="ux-iws-nav__meta-container-burger">
                                        <ux-iws-nav-burger
                                            menu-text-open={menuLabel}
                                            menu-text-close={closeLabel}
                                        ></ux-iws-nav-burger>
                                    </div>
                                </div>
                            </ux-iws-nav>
                        </div>
                    </div>
                    <div
                        slot="header-dropdown"
                        id="ux-iws-header__dropdown-container"
                        class="ux-iws-header__dropdown-container"
                    >
                        <div class="ux-iws-header__meta-container-dropdown">
                            <ux-iws-navigation-meta>
                                {this.renderProfileLink()}
                                {this.renderCountryLink()}
                                <span class="ux-iws-navigation-meta__item__flex-break"></span>
                                {this.renderHomeLink(true)}
                            </ux-iws-navigation-meta>
                        </div>
                    </div>
                </ux-iws-header>
                <div class="is-hidden" aria-hidden="true">
                    <slot name="profile-links" />
                </div>
            </Host>
        );
    }
}
